// Iter191 · Página "Restablecer contraseña"
// Consume el token del query string y envía la nueva contraseña.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token") || "";
    setToken(t);
    // Retiramos el token de la URL para que no quede en el historial ni referrer
    if (t) window.history.replaceState({}, "", "/reset-password");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return setError("Enlace inválido. Solicita un nuevo email de recuperación.");
    if (pwd.length < 8) return setError("La contraseña debe tener al menos 8 caracteres.");
    if (pwd !== pwd2) return setError("Las contraseñas no coinciden.");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "El enlace no es válido o ha caducado.");
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-br from-[#007934] to-emerald-600 p-6 text-white">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/15 mb-3">
            <Lock size={22} />
          </div>
          <h1 className="text-xl font-bold">Nueva contraseña</h1>
          <p className="text-sm opacity-85 mt-1">Introduce tu nueva contraseña y confírmala.</p>
        </div>
        <div className="p-6">
          {!token && (
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              <AlertTriangle size={16} className="mt-0.5"/>
              <div>
                No se detectó un token de recuperación. Solicita un enlace nuevo desde{" "}
                <Link to="/forgot-password" className="underline font-semibold">¿Olvidé mi contraseña?</Link>.
              </div>
            </div>
          )}
          {done ? (
            <div data-testid="reset-done-msg" className="text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 size={28}/>
              </div>
              <div className="text-sm text-slate-700">Contraseña actualizada correctamente.</div>
              <div className="text-xs text-slate-500">Redirigiendo al login…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-700 mb-1">Nueva contraseña</span>
                <input
                  type="password" required minLength={8} autoFocus
                  value={pwd} onChange={e => setPwd(e.target.value)}
                  placeholder="Al menos 8 caracteres"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#007934] focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                  data-testid="reset-pwd-input"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-700 mb-1">Repite la contraseña</span>
                <input
                  type="password" required minLength={8}
                  value={pwd2} onChange={e => setPwd2(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#007934] focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                  data-testid="reset-pwd2-input"
                />
              </label>
              {error && <div className="text-sm text-rose-600" role="alert" data-testid="reset-error">{error}</div>}
              <button
                type="submit" disabled={loading || !token}
                className="w-full bg-[#007934] text-white rounded-lg py-2.5 font-semibold hover:bg-[#006630] transition disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin"/> : <Lock size={16}/>}
                {loading ? "Guardando..." : "Guardar contraseña"}
              </button>
              <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-slate-700">
                Volver al login
              </Link>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
